import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { BaseRespone } from 'app/core/models/common.response';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, startWith, take, takeUntil } from 'rxjs/operators';
import { InwardReport } from 'app/modules/admin/transactions/inwards/reports.types';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { LookupService } from 'app/core/common/services/lookup.service';
import { CommonDropDown } from 'app/core/common/types/dropdown.types';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SiteMeasurementsService } from '../site-measurement.service';
import { SiteMeasurement } from '../site-measurement.types';

@Component({
    selector: 'app-builtup-area-site-measurement',
    templateUrl: './builtup-area-site-measurement.component.html',
})
export class BuiltupAreaSiteMeasurementComponent implements OnInit {

    @Output('calculationAreas') calculationAreas: EventEmitter<any> =
        new EventEmitter();
    selection = new SelectionModel<SiteMeasurement>(true, []);
    @Input() report: InwardReport;
    @Input() reportId: number;
    @Input() reportType: string;

    totalArea: number = 0.0;
    totalBuiltUp: number = 0.0;
    totalSuperBuiltUp: number = 0.0;

    selectedSiteMeasurement: SiteMeasurement;
    selectedIds: number[] = [];
    isAllSelectedCheckBox: boolean = false;
    builtUpSiteMeasurements$: Observable<SiteMeasurement[]>;
    options: CommonDropDown[] = [];
    filteredOptions: { [index: number]: CommonDropDown[] } = {};
    siteMeasurementForm: FormGroup;
    builtUpAreaType: string = 'Builtup Area';
    alert: any;


    private _unsubscribeAll: Subject<any> = new Subject<any>();
    constructor(
        private _matDialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private _siteMeasurementService: SiteMeasurementsService,
        private _alertMessageService: AlertMessageService,
        private _lookupService: LookupService,
        private _formBuilder: FormBuilder
    ) { }

    ngOnInit(): void {
        this._siteMeasurementService
            .getBuiltUpSiteMeasurements(this.reportId)
            .subscribe((res) => {
                this.calculateSiteMeasurementsTotal();
            });

        this.builtUpSiteMeasurements$ =
            this._siteMeasurementService.builtUpSiteMeasurements$.pipe(
                map((data) => {
                    data?.sort((a, b) => a.orderIndex - b.orderIndex);

                    // Initialize filteredOptions for each row
                    if (data) {
                        data.forEach((m, i) => {
                            this.filterParticularOptions(m, i);
                        });
                    }
                    return data;
                })
            );


        // Get the contact
        this._siteMeasurementService.siteMeasurement$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((contact: SiteMeasurement) => {
                // Update the selected contact
                this.selectedSiteMeasurement = contact;

                // Mark for check
                //this._changeDetectorRef.markForCheck();
            });

        this.siteMeasurementForm = this._formBuilder.group({
            id: [''],
            reportId: [this.reportId],
            type: [this.builtUpAreaType, [Validators.required]],
            particular: [''],
            width: [0],
            depth: [0],
            total: [0],
        });

        this._lookupService
            .getOptionByTypeId(91, this.reportType)
            .subscribe((res) => {
                this.options = res;

                // Now initialize filteredOptions for each row if measurements are already loaded
                this.builtUpSiteMeasurements$.pipe(take(1)).subscribe(siteMeasurements => {
                    if (siteMeasurements) {
                        siteMeasurements.forEach((m, i) => {
                            this.filterParticularOptions(m, i);
                        });
                    }
                });
            });
    }

    displayOptionsFn = (value: any): string => {
        if (!value) return '';
        // If value is a string, find the option object
        if (typeof value === 'string') {
            const match = this.options.find(opt => opt.name === value);
            return match ? match.name : value;
        }
        // If value is an object
        return value.name || '';
    };

    private _optionsfilter(name: string): CommonDropDown[] {
        const filterValue = name.toString().toLowerCase();

        return this.options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
        );
    }

    new(selectedValue?: string): void {
        this._siteMeasurementService.addNewMeasurement(this.reportId, selectedValue);
    }

    cancelNewRow(index: number): void {
        this._siteMeasurementService.cancelBuiltRow(index);
    }

    save(sitemeasurement: SiteMeasurement): void {
        if (
            !sitemeasurement.particular ||
            sitemeasurement.particular.trim() === ''
        ) {
            this.alert = {
                type: 'error',
                message: 'Please select particular'
            };
            return;
        }
        if (sitemeasurement.id !== null && sitemeasurement.id !== 0) {
            this._siteMeasurementService
                .updateSiteMeasurement(sitemeasurement, this.builtUpAreaType)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        // Show a success message...
                        this.alert = {
                            type: 'success',
                            message: 'Document Measurement updated successfully!'
                        };
                        this.calculateSiteMeasurementsTotal();
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        } else {
            this._siteMeasurementService
                .createSiteMeasurement(sitemeasurement, this.builtUpAreaType)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        this.alert = {
                            type: 'success',
                            message: 'Site measurement added successfully'
                        };
                        this.calculateSiteMeasurementsTotal();
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        }
    }

    calculateTotal(measurement: SiteMeasurement): void {
        if (measurement.width && measurement.depth) {
            measurement.total = Number(
                (measurement.width * measurement.depth).toFixed(2)
            );
        } else {
            measurement.total = 0;
        }
    }

    /**
     * Delete the selected product using the form data
     */
    delete(id: number): void {
        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Site Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if (result === 'confirmed') {
                //Delete the product on the server
                this._siteMeasurementService
                    .deleteSiteMeasurement(id, this.builtUpAreaType)
                    .subscribe((res) => {
                        if (res.isSuccess) {
                            this.alert = {
                                type: 'success',
                                message: 'Site measurement deleted successfully'
                            };
                            this.calculateSiteMeasurementsTotal();
                            // this.updateOrderIndex();
                            this.builtUpSiteMeasurements$.pipe(take(1)).subscribe(data => {
                                if (data) {
                                    this.updateOrderIndex(data);
                                }
                            });
                        } else {
                            this._alertMessageService.error(
                                res.errors.join('\n')
                            );
                        }
                    });
            }
        });
    }
    async deleteSelectedRows() {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete All Site Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        const result = await confirmation.afterClosed().toPromise();

        if (result === 'confirmed') {
            const ids = this.selection.selected.map((item) => item.id);
            const res = await this._siteMeasurementService
                .deleteAllSiteMeasurement(ids, this.builtUpAreaType)
                .toPromise();

            if (res.isSuccess) {
                this.alert = {
                    type: 'success',
                    message: 'Site measurement all deleted successfully'
                };
                this.calculateSiteMeasurementsTotal();
                this.builtUpSiteMeasurements$.pipe(take(1)).subscribe(data => {
                    if (data) {
                        this.updateOrderIndex(data);
                    }
                });
                this.selection.clear();
                this.builtUpSiteMeasurements$.subscribe();
            } else {
                this.alert = {
                    type: 'error',
                    message: res.errors.join('\n')
                };
            }
        }
    }
    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    calculateSiteMeasurementsTotal(): void {
        this._siteMeasurementService.builtUpSiteMeasurements$.pipe(take(1)).subscribe((res) => {
            // Only Builtup Area
            this.report.siteBuiltUp = Number(
                res
                    .filter((a) => a.type === this.builtUpAreaType)
                    .reduce((sum, current) => sum + current.total, 0)
                    .toFixed(2)
            );

            this.calculationAreas.emit();
        });
    }

    updateCheckedList(event, row) {
        this.builtUpSiteMeasurements$.pipe(take(1)).subscribe((measurements) => {
            measurements.forEach((item) => {
                if (item.id == row.id) {
                    item.selected = event.checked;
                    if (item.selected) {
                        this.selection.select(item); // Select item in selection model
                    } else {
                        this.selection.deselect(item);
                    }
                }
            });

            this.isAllSelectedCheckBox = !measurements.some((a) => !a.selected);
        });
    }

    selectAll(event: any) {
        const checked = event.checked;
        this.builtUpSiteMeasurements$
            .pipe(take(1))
            .subscribe((measurements: any[]) => {
                measurements.forEach((measurement) => {
                    measurement.selected = checked;
                });
            });
    }
    checkboxLabel() {
        if (this.isAllSelected()) {
            return 'Deselect all';
        }
        if (this.selection.isEmpty()) {
            return 'Select all';
        }
        return `${this.selection.selected.length} selected`;
    }

    isAllSelected() {
        // Check if all measurements are selected
        return this.builtUpSiteMeasurements$.pipe(
            take(1),
            map((measurements) =>
                measurements.every((measurement) => measurement.selected)
            )
        );
    }

    toggleAllRows() {
        this.isAllSelected().subscribe((allSelected) => {
            if (!allSelected) {
                // Select all items
                this.builtUpSiteMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = true;
                            this.selection.select(item); // Select item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = true;
            } else {
                // Deselect all items
                this.builtUpSiteMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = false;
                            this.selection.deselect(item); // Deselect item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = false;
            }
        });
    }


    drop(event: CdkDragDrop<SiteMeasurement[]>): void {
        // Retrieve current data from the observable
        this.builtUpSiteMeasurements$.pipe(take(1)).subscribe((data) => {
            if (data) {
                moveItemInArray(data, event.previousIndex, event.currentIndex);

                this.updateOrderIndex(data);
            }
        });
    }

    updateOrderIndex(data: SiteMeasurement[]): void {
        data.forEach((item, index) => {
            item.orderIndex = index + 1; // Reset order index
            this._siteMeasurementService.updateSiteMeasurement(item).subscribe();
        });
    }

    originalValues = new Map<string, any>();

    getKey(siteMeasurement: any, field: string): string {
        return (siteMeasurement.id ?? siteMeasurement.tempId ?? JSON.stringify(siteMeasurement)) + '_' + field;
    }

    // Called when field is focused to store the original value
    onInputInit(siteMeasurement: any, field: string) {
        const key = this.getKey(siteMeasurement, field);
        if (!this.originalValues.has(key)) {
            this.originalValues.set(key, this.cloneValue(siteMeasurement[field]));
        }
    }

    // Called on each value change
    onFieldChange(siteMeasurement: any, field: string) {
        const key = this.getKey(siteMeasurement, field);
        const original = this.originalValues.get(key);
        const current = siteMeasurement[field];

        if (!this.deepEqual(original, current)) {
            this.save(siteMeasurement); // auto-save logic
            this.originalValues.set(key, this.cloneValue(current)); // update cache
        }
    }

    // Utility: deep compare for objects/arrays/primitives
    deepEqual(a: any, b: any): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    // Utility: clone value (for caching original values safely)
    cloneValue(val: any): any {
        return JSON.parse(JSON.stringify(val));
    }

    filterParticularOptions(siteMeasurement: any, index: number) {
        if (!this.options) {
            this.filteredOptions[index] = [];
            return;
        }
        const value = siteMeasurement.particular || '';
        const filterValue = value.toLowerCase();
        this.filteredOptions[index] = this.options.filter(option =>
            option.name.toLowerCase().includes(filterValue)
        );
    }

    // Called when user selects from autocomplete
    onAutocompleteSelected(siteMeasurement: any, value: string) {
        siteMeasurement.particular = value;
        this.onFieldChange(siteMeasurement, 'particular');
    }
}
